import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';

const AuthContext = createContext();

// Use relative URL to work with nginx proxy
const API_URL = process.env.REACT_APP_API_URL || '';

// Configure axios defaults
axios.defaults.baseURL = API_URL;

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  // Configure axios to include token in all requests
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Load user on mount
  useEffect(() => {
    const loadUser = async () => {
      const savedToken = localStorage.getItem('token');
      if (savedToken) {
        try {
          setToken(savedToken);
          axios.defaults.headers.common['Authorization'] = `Bearer ${savedToken}`;
          const response = await axios.get('/api/auth/me');
          setCurrentUser(response.data);
        } catch (error) {
          console.error('Failed to load user:', error);
          localStorage.removeItem('token');
          setToken(null);
        }
      }
      setLoading(false);
    };
    loadUser();
  }, []);

  const login = async (username, password) => {
    try {
      const response = await axios.post('/api/auth/login', {
        username,
        password
      });
      const { access_token } = response.data;
      
      // Store token
      localStorage.setItem('token', access_token);
      setToken(access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      // Get user info
      const userResponse = await axios.get('/api/auth/me');
      setCurrentUser(userResponse.data);
      
      return userResponse.data;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const register = async (designation, cugNumber, email, password, verificationToken, emailVerificationToken) => {
    try {
      const response = await axios.post('/api/auth/register', {
        designation,
        username: cugNumber,
        email,
        password,
        verification_token: verificationToken,
        email_verification_token: emailVerificationToken,
      });
      return response.data;
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    }
  };

  // ---- OTP helpers (registration — mobile) ----
  const sendRegistrationOtp = async (mobile, email) => {
    const response = await axios.post('/api/auth/register/send-otp', {
      mobile,
      ...(email ? { email } : {}),
    });
    return response.data;
  };

  const verifyRegistrationOtp = async (mobile, otp) => {
    const response = await axios.post('/api/auth/register/verify-otp', { mobile, otp });
    return response.data; // { verification_token, expires_in }
  };

  // ---- OTP helpers (registration — email) ----
  const sendRegistrationEmailOtp = async (email) => {
    const response = await axios.post('/api/auth/register/send-email-otp', { email });
    return response.data;
  };

  const verifyRegistrationEmailOtp = async (email, otp) => {
    const response = await axios.post('/api/auth/register/verify-email-otp', { email, otp });
    return response.data; // { verification_token, expires_in }
  };

  // ---- Forgot-password helpers ----
  const forgotPasswordSendOtp = async (identifier, channel = 'sms') => {
    const response = await axios.post('/api/auth/forgot-password/send-otp', { identifier, channel });
    return response.data;
  };

  const forgotPasswordVerifyOtp = async (identifier, otp) => {
    const response = await axios.post('/api/auth/forgot-password/verify-otp', { identifier, otp });
    return response.data; // { verification_token, expires_in }
  };

  const forgotPasswordReset = async (resetToken, newPassword) => {
    const response = await axios.post('/api/auth/forgot-password/reset', {
      reset_token: resetToken,
      new_password: newPassword,
    });
    return response.data;
  };

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    setToken(null);
    setCurrentUser(null);
    delete axios.defaults.headers.common['Authorization'];
  }, []);

  // 401 interceptor — auto-logout on expired/invalid token
  const interceptorRef = useRef(null);
  useEffect(() => {
    // Eject previous interceptor if any
    if (interceptorRef.current !== null) {
      axios.interceptors.response.eject(interceptorRef.current);
    }
    interceptorRef.current = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401 && token) {
          toast.error('Session expired. Please sign in again.');
          logout();
        }
        return Promise.reject(error);
      }
    );
    return () => {
      if (interceptorRef.current !== null) {
        axios.interceptors.response.eject(interceptorRef.current);
      }
    };
  }, [token, logout]);

  const value = {
    currentUser,
    login,
    register,
    logout,
    loading,
    token,
    sendRegistrationOtp,
    verifyRegistrationOtp,
    sendRegistrationEmailOtp,
    verifyRegistrationEmailOtp,
    forgotPasswordSendOtp,
    forgotPasswordVerifyOtp,
    forgotPasswordReset,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
